/**
 * CohortFilterFields — the three cohort fields inside a `FilterToolbar`
 * dialog: Country · Cohort year · Cycle, on the dialog's two-column grid with
 * `TOOLBAR_FIELD_CLASS`, so they read as part of the page's one form.
 *
 * One component rather than three copies because six pages carry the same
 * three fields (Families roster and Needs you, Schedule, Threads, Unreads —
 * Awards and Balances already had their own since 2026-09-14). The state is
 * the page's URL through `useCohortFilters`; nothing here owns a value.
 *
 * Cohort year commits on blur or Enter, as the retired header popover did,
 * so a half-typed "20" never reaches the server; the range is enforced here
 * because `min`/`max` are inert on a text input (see `numericInputProps`).
 */
import { useEffect, useState } from 'react';
import type { CohortFilters } from '@/hooks/useCohortFilters';
import { COHORT_MAX_YEAR, COHORT_MIN_YEAR } from '@/lib/cohort';
import { TOOLBAR_FIELD_CLASS } from './FilterToolbar';
import { digitsOnly, numericInputProps } from './numericInput';

const LABEL_CLASS = 'flex flex-col gap-1.5 text-body font-medium';

export function CohortFilterFields({ cohort }: { cohort: CohortFilters }) {
  const applied = cohort.cohortYear != null ? String(cohort.cohortYear) : '';
  const [yearDraft, setYearDraft] = useState(applied);
  // A pill clear or a fresh address resets the draft to what is applied.
  useEffect(() => { setYearDraft(applied); }, [applied]);

  const commitYear = () => {
    const v = yearDraft.trim();
    if (!v) { if (cohort.cohortYear != null) cohort.set({ cohortYear: null }); return; }
    const n = Number(v);
    if (v.length === 4 && n >= COHORT_MIN_YEAR && n <= COHORT_MAX_YEAR) {
      if (n !== cohort.cohortYear) cohort.set({ cohortYear: n });
    } else {
      setYearDraft(applied);
    }
  };

  return (
    <>
      <label className={LABEL_CLASS}>
        Country
        <select
          aria-label="Country"
          className={TOOLBAR_FIELD_CLASS}
          value={cohort.country ?? ''}
          onChange={(e) => cohort.set({ country: e.target.value === 'US' || e.target.value === 'CA' ? e.target.value : null })}
        >
          <option value="">All</option>
          <option value="US">US</option>
          <option value="CA">CA</option>
        </select>
      </label>
      <label className={LABEL_CLASS}>
        Cohort year
        <input
          {...numericInputProps({ max: COHORT_MAX_YEAR })}
          aria-label="Cohort year"
          className={TOOLBAR_FIELD_CLASS}
          placeholder="Any"
          value={yearDraft}
          onChange={(e) => setYearDraft(digitsOnly(e.target.value))}
          onBlur={commitYear}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitYear();
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </label>
      <label className={LABEL_CLASS}>
        Cycle
        <select
          aria-label="Cycle"
          className={TOOLBAR_FIELD_CLASS}
          value={cohort.cycle ?? ''}
          onChange={(e) => cohort.set({ cycle: e.target.value === 'I' || e.target.value === 'II' ? e.target.value : null })}
        >
          <option value="">All</option>
          <option value="I">I</option>
          <option value="II">II</option>
        </select>
      </label>
    </>
  );
}
